import { useEffect, useState } from "react";
import type { RecipeDetail } from "./queries";

interface RecipeEditorState {
  body: string;
  tags: string[];
  trainingConfig: Record<string, unknown>;
  isDirty: boolean;
  currentEtag: string;
}

export function useRecipeEditor(recipe: RecipeDetail | undefined) {
  const [state, setState] = useState<RecipeEditorState>({
    body: "",
    tags: [],
    trainingConfig: {},
    isDirty: false,
    currentEtag: "",
  });

  // Sync with recipe when it loads or changes
  useEffect(() => {
    if (!recipe) return;

    setState({
      body: recipe.body,
      tags: [...recipe.tags],
      trainingConfig: { ...(recipe.training_config || {}) },
      isDirty: false,
      currentEtag: recipe.etag,
    });
  }, [recipe]);

  const setBody = (body: string) => {
    setState((prev) => ({
      ...prev,
      body,
      isDirty: true,
    }));
  };

  const setTags = (tags: string[]) => {
    setState((prev) => ({
      ...prev,
      tags,
      isDirty: true,
    }));
  };

  const setTrainingConfig = (trainingConfig: Record<string, unknown>) => {
    setState((prev) => ({
      ...prev,
      trainingConfig,
      isDirty: true,
    }));
  };

  const reset = () => {
    if (!recipe) return;
    setState({
      body: recipe.body,
      tags: [...recipe.tags],
      trainingConfig: { ...(recipe.training_config || {}) },
      isDirty: false,
      currentEtag: recipe.etag,
    });
  };

  const updateEtag = (etag: string) => {
    setState((prev) => ({
      ...prev,
      currentEtag: etag,
      isDirty: false,
    }));
  };

  return {
    body: state.body,
    tags: state.tags,
    trainingConfig: state.trainingConfig,
    isDirty: state.isDirty,
    currentEtag: state.currentEtag,
    setBody,
    setTags,
    setTrainingConfig,
    reset,
    updateEtag,
  };
}
